import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  BusSeatChoice,
  busSeatFailureOf,
  busSeatMapStates,
  personInitials,
  seatClassGlyphOf,
  type BusLegCities,
  type BusSeatFailure,
  type BusSeatLayout,
  type BusSeatPickerCopy,
  type BusSeatPreference,
  type BusSeatsSource,
  type BusSegmentPick,
} from '@voll-intelligence/intelligence-ui-kit';
import { fmtMoney } from '../../../shared/ui/flight_format.js';
import { useGenUiI18n } from '../../../shared/ui/i18n.js';
import { useBusSeatsPort } from '../../shared/bus_seats_port.js';
import type { BusOfferSegmentSelection, ChatUiBusOffer } from '@voll-intelligence/genui-contract';

type T = ReturnType<typeof useGenUiI18n>['t'];

/** A chave de quem conversa na planta, quando o bloco não traz ids (um
 *  viajante só). */
export const TRAVELER_ID = 'traveler';

/** Quem viaja, como a planta o conhece: o id (a chave da poltrona) e o nome. */
export type BusSeatTraveler = { readonly id: string; readonly name: string };

/** A leitura do preço no rodapé da planta: por pessoa, e de qual perna. */
export type BusSeatPriceScope = 'oneWay' | 'outbound' | 'return';

type SeatsState = {
  status: 'pending' | 'error' | 'success';
  data?: BusSeatsSource;
  failure?: BusSeatFailure;
};

const PENDING_SEATS: SeatsState = { status: 'pending' };
const NO_PORT_SEATS: SeatsState = { status: 'error' };

/** A planta da viagem pelo port do host, e o "tentar de novo" (`retry`) que
 *  pede de novo ao port. A rejeição diz o motivo (`busSeatFailureOf`). Sem
 *  port, cada trecho é erro sem retry: a saída é seguir sem assento, nunca uma
 *  planta que não carrega para sempre. */
function useBusSeats(offerId: string): SeatsState & { retry?: () => void } {
  const port = useBusSeatsPort();
  const [attempt, setAttempt] = useState(0);
  // A resposta carrega o pedido que respondeu: outra oferta (ou nova tentativa)
  // volta a `pending` no mesmo render.
  const [answer, setAnswer] = useState<{
    port: typeof port;
    offerId: string;
    attempt: number;
    state: SeatsState;
  } | null>(null);
  useEffect(() => {
    if (!port) return undefined;
    let live = true;
    const answered = (state: SeatsState) => {
      if (live) setAnswer({ port, offerId, attempt, state });
    };
    port.getSeats(offerId).then(
      (data) => {
        answered({ status: 'success', data });
      },
      (error: unknown) => {
        answered({ status: 'error', failure: busSeatFailureOf(error) });
      },
    );
    return () => {
      live = false;
    };
  }, [port, offerId, attempt]);
  if (!port) return NO_PORT_SEATS;
  const state =
    answer?.port === port && answer.offerId === offerId && answer.attempt === attempt
      ? answer.state
      : PENDING_SEATS;
  return {
    ...state,
    retry: () => {
      setAttempt((n) => n + 1);
    },
  };
}

/** As Preferências de ônibus que sugerem a poltrona. Falha ou ausência = sem
 *  sugestão. */
function useSeatPreference(): BusSeatPreference | undefined {
  const port = useBusSeatsPort();
  const [preference, setPreference] = useState<BusSeatPreference>();
  useEffect(() => {
    if (!port?.getSeatPreference) return undefined;
    let live = true;
    port.getSeatPreference().then(
      (read) => {
        if (live && read) setPreference(read);
      },
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [port]);
  return preference;
}

/** O percurso de cada trecho no resumo da planta (#4861): origem → parada,
 *  parada → destino. Sem a parada da conexão, o resumo fica com o número. */
function segmentRouteLabelOf(
  t: T,
  offer: ChatUiBusOffer,
  cities: BusLegCities,
): Pick<BusSeatPickerCopy, 'segmentRouteLabel'> {
  const stop = offer.connections > 0 ? offer.connection?.stopName : undefined;
  if (!stop) return {};
  const routes = [
    { from: cities.departure, to: stop },
    { from: stop, to: cities.arrival },
  ];
  return {
    segmentRouteLabel: (segment) => {
      const route = routes[segment - 1];
      return route
        ? t('busSeats.segmentRoute', { segment, ...route })
        : t('busSeats.segment', { segment });
    },
  };
}

function busSeatCopy(
  t: T,
  routes: Pick<BusSeatPickerCopy, 'segmentRouteLabel'>,
): BusSeatPickerCopy {
  return {
    segmentsLabel: t('busSeats.segments'),
    segmentLabel: (segment) => t('busSeats.segment', { segment }),
    ...routes,
    entranceLabel: t('busSeats.entrance'),
    driverLabel: t('busSeats.driver'),
    floorLabel: (floor) => t('busSeats.floor', { floor }),
    seatLabel: (code, occupied) =>
      occupied ? t('busSeats.seatOccupied', { code }) : t('busSeats.seat', { code }),
    loadingLabel: t('busSeats.loading'),
    errorLabel: t('busSeats.error'),
    offlineLabel: t('busSeats.offline'),
    notOfferedLabel: t('busSeats.notOffered'),
    retryLabel: t('busSeats.retry'),
    fullLabel: t('busSeats.full'),
    noSeatLabel: t('busSeats.noSeat'),
    noSeatSummary: t('busSeats.noSeatSummary'),
    pendingSeatsLabel: (count) =>
      t(count === 1 ? 'busSeats.pending_one' : 'busSeats.pending_other', { count }),
    confirmLabel: t('busSeats.confirm'),
    collapseLabel: t('busSeats.collapse'),
    details: {
      legend: {
        free: t('busSeats.legend.free'),
        occupied: t('busSeats.legend.occupied'),
        selected: t('busSeats.legend.selected'),
        recommended: t('busSeats.legend.recommended'),
        window: t('busSeats.legend.window'),
      },
      window: t('busSeats.detail.window'),
      aisle: t('busSeats.detail.aisle'),
      windowAndAisle: t('busSeats.detail.windowAndAisle'),
      individual: t('busSeats.detail.individual'),
      area: (area) => t(`busSeats.detail.area.${area}`),
      neighborFree: t('busSeats.detail.neighborFree'),
      neighborOccupied: t('busSeats.detail.neighborOccupied'),
      recommended: t('busSeats.detail.recommended'),
      hint: t('busSeats.detail.hint'),
    },
  };
}

/** A resolução de um trecho no fio: sem assento; a poltrona de quem viaja só;
 *  ou, com vários viajantes, a poltrona de cada um por id. */
function toLegSelection(
  pick: BusSegmentPick,
  travelers: readonly BusSeatTraveler[],
): BusOfferSegmentSelection {
  if (pick.kind === 'noSeat') return { noSeat: true };
  if (travelers.length > 1) return { seats: { ...pick.seats } };
  const seatCode = travelers[0] ? pick.seats[travelers[0].id] : undefined;
  return seatCode ? { seatCode } : { noSeat: true };
}

/**
 * A escolha de poltrona POR TRECHO de uma viagem no card de partidas da
 * conversa — o `BusSeatChoice` da bancada com a planta fresca da viagem (port
 * `busSeats`) e a sugestão das Preferências. Uma poltrona por trecho para cada
 * viajante, ou "seguir sem assento".
 */
export function BusOfferSeats({
  offer,
  cities,
  travelers,
  scope,
  layout,
  onConfirm,
  onCollapse,
}: Readonly<{
  offer: ChatUiBusOffer;
  cities: BusLegCities;
  /** Quem viaja — as iniciais na poltrona escolhida e a chave de cada uma. */
  travelers: readonly BusSeatTraveler[];
  scope: BusSeatPriceScope;
  layout: BusSeatLayout;
  onConfirm: (segments: BusOfferSegmentSelection[]) => void;
  onCollapse: () => void;
}>): ReactNode {
  const { t, locale } = useGenUiI18n();
  const seats = useBusSeats(offer.offerId);
  const preference = useSeatPreference();
  const { departure, arrival } = cities;
  const copy = busSeatCopy(t, segmentRouteLabelOf(t, offer, { departure, arrival }));
  const people = useMemo(
    () =>
      travelers.map(({ id, name }) => ({
        id,
        name,
        initials: name ? personInitials(name, locale) : '',
      })),
    [travelers, locale],
  );
  return (
    <BusSeatChoice
      segments={busSeatMapStates(offer.connections, seats)}
      {...(seats.retry ? { onRetry: seats.retry } : {})}
      travelers={people}
      {...(preference ? { preference } : {})}
      suggestionLabel={t('busSeats.suggested')}
      seatClassLabel={offer.seatClass}
      price={{ label: fmtMoney(offer.price), detail: t(`busSeats.price.${scope}`) }}
      copy={copy}
      layout={layout}
      seatGlyph={seatClassGlyphOf(offer.seatClass)}
      onConfirm={(segments) => {
        onConfirm(segments.map((pick) => toLegSelection(pick, travelers)));
      }}
      onCollapse={onCollapse}
    />
  );
}
